import React, { useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import clsx from 'clsx';

import { AccordionSectionHeader, AccordionSectionDetails } from 'src/components/accordion-section';
import Responsive from 'src/components/responsive/Responsive';
import { Flex } from 'src/components/flex';
import { SimpleDesktopConversationsTable } from 'src/pages/landlord/conversations/components/list/FreshnessGroupAccordion';
import { MobileConversationListItem } from 'src/pages/landlord/conversations/components/list/ConversationListItem';
import { type IConversation } from 'src/api/landlord-messages-api';
import { type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';

import categoryStyles from 'src/pages/landlord/conversations/components/list/FreshnessGroupAccordion.module.css';

const ROWS_PER_PAGE = 25;

type PropertyGroupProps = {
    title: string;
    conversations: IConversation[];
    leadsByUuid: Map<string, any>;
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    focusedConversation?: IConversation;
    setFocusedConversation: React.Dispatch<React.SetStateAction<IConversation | undefined>>;
    property?: any;
    setMobileMenuAnchorEl: (el: HTMLElement | undefined) => void;
    variant?: 'default' | 'longPreview';
};

export default function PropertyGroupAccordion({
    title,
    conversations,
    leadsByUuid,
    selectedConversations,
    setSelectedConversations,
    focusedConversation,
    setFocusedConversation,
    property,
    setMobileMenuAnchorEl,
    variant = 'default',
}: PropertyGroupProps) {
    const [expanded, setExpanded] = useState(true);
    const [page, setPage] = useState(1);

    const pageCount = Math.ceil(conversations.length / ROWS_PER_PAGE);
    const startIndex = (page - 1) * ROWS_PER_PAGE + 1;
    const endIndex = Math.min(page * ROWS_PER_PAGE, conversations.length);
    const visibleConversations = conversations.slice((page - 1) * ROWS_PER_PAGE, endIndex);

    const allSelected = conversations.length > 0 &&
        visibleConversations.every((c) =>
            selectedConversations.some((s) => s.lead_uuid === c.lead_uuid)
        );

    const selectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (allSelected) {
            setSelectedConversations(prev =>
                prev.filter(c => !visibleConversations.some(v => v.lead_uuid === c.lead_uuid))
            );
        } else {
            setSelectedConversations(prev => {
                const toAdd = visibleConversations.filter(c => !prev.some(s => s.lead_uuid === c.lead_uuid));
                return [...prev, ...toAdd];
            });
        }
    };

    useEffect(() => {
        setPage(1);
    }, [conversations.length]);

    const handlePageChange = (newPage: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setPage(newPage);
        setSelectedConversations([]);
        setFocusedConversation(undefined);
    };

    return (
        <Accordion
            className={categoryStyles.accordionRoot}
            disableGutters
            square
            elevation={0}
            expanded={expanded}
            onChange={(_, val) => setExpanded(val)}
        >
            <AccordionSectionHeader
                expanded={expanded}
                title={title}
                className={clsx(
                    categoryStyles.header,
                    conversations.length > 0 && categoryStyles.headerHasConversations,
                    selectedConversations.length > 0 && categoryStyles.headerHasSelection,
                )}
                leading={(
                    <Responsive desktop>
                        <Box className={categoryStyles.checkboxSpacer}>
                            <Checkbox
                                checked={allSelected}
                                onClick={selectAll}
                                className={categoryStyles.checkbox}
                            />
                        </Box>
                    </Responsive>
                )}
                trailing={pageCount > 0 ? (
                    <Flex alignItems="center" onClick={e => e.stopPropagation()}>
                        <Typography variant="caption">
                            {startIndex} – {endIndex} of {conversations.length}
                        </Typography>
                        <Flex className={categoryStyles.pageControls}>
                            <IconButton
                                onClick={e => handlePageChange(page - 1, e)}
                                disabled={page === 1}
                                size="small"
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                                onClick={e => handlePageChange(page + 1, e)}
                                disabled={page === pageCount}
                                size="small"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Flex>
                    </Flex>
                ) : null}
            />

            <Responsive desktop>
                <AccordionSectionDetails isEmpty={conversations.length === 0} emptyMessage="No conversations">
                    <SimpleDesktopConversationsTable
                        conversations={visibleConversations}
                        navigationConversations={conversations}
                        leadsByUuid={leadsByUuid}
                        selectedConversations={selectedConversations}
                        setSelectedConversations={setSelectedConversations}
                        focusedConversation={focusedConversation}
                        setFocusedConversation={setFocusedConversation}
                        variant={variant}
                    />
                </AccordionSectionDetails>
            </Responsive>

            <Responsive mobile>
                <AccordionSectionDetails isEmpty={conversations.length === 0} emptyMessage="No conversations">
                    {visibleConversations.map(conversation => {
                        return (
                            <MobileConversationListItem
                                key={conversation.lead_uuid}
                                conversation={conversation}
                                property={property}
                                navigationConversations={conversations}
                                leadsByUuid={leadsByUuid}
                                setMobileMenuAnchorEl={setMobileMenuAnchorEl as (el: HTMLElement) => void}
                                setSelectedConversations={setSelectedConversations}
                            />
                        );
                    })}
                </AccordionSectionDetails>
            </Responsive>
        </Accordion>
    );
}
